import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import InfiniteScroll from "react-infinite-scroll-component";
import { IconButton, InputBase } from "@mui/material";
import { NearMeOutlined } from "@mui/icons-material";
import moment from "moment";

import Modal, { ModalBody, ModalFooter } from "Components/Modal";
import { close, setIsExpanded } from "Modules/chat/views/chatsContainer/redux/slice";
import useChatContainerHeight from "Modules/chat/hooks/useChatContainerHeight";
import { AIMessageBubbleType, AIStage, AiMessageType, InitializationState } from "../constants";
import { fetchMoreMessages } from "../redux/aiChatListSlice";

import "Modules/chat/index.scss";
import SkeletonChatMessages from "Modules/chat/views/chatThread/components/SkeletonChatMessages";
import AIChatMessages from "./AIChatMessages";

const INPUT_MAX_LENGTH = 500;

// Height constants for container calculation
const HEADER_HEIGHT = 64;
const FOOTER_BASE_HEIGHT = 90;
const MARGIN_BUFFER = 0;
const INPUT_WITH_COUNTER_BUFFER = 50;
const INPUT_WITHOUT_COUNTER_BUFFER = 34;

const AIMessageList = ({
  messages,
  openModal,
  onClose,
  items = [],
  submitQuestion,
  loadingState,
  loading,
  currentStage,
  stageMessage, // Message from backend
  isStreaming,
  error,
  initializationState,
  handleRetryPrompt,
  subscriptionConnected = false, // WebSocket connection status
  subscriptionConnecting = false, // WebSocket connecting status
}) => {
  const [question, setQuestion] = useState("");
  const [chatStartedLabel, setChatStartedLabel] = useState("");
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [oldScrollTop, setOldScrollTop] = useState(0);
  const isLoadingHistoryRef = useRef(false);
  const wasAtBottomRef = useRef(true); // Track if user was at bottom before update
  const justSubmittedRef = useRef(false); // Track if user just submitted a message
  const previousStreamingRef = useRef(false); // Track previous streaming state
  const previousStageRef = useRef(null); // Track previous stage
  const dispatch = useDispatch();

  const { nextToken, hasMoreMessages, conversationId } = useSelector((state) => state.aiChatListRedux);
  const { isExpanded } = useSelector((state) => state.chatsContainerRedux);

  // Use shared height calculation hook
  const { inputRef, scrollRef, bodyRef, recalculate, showInputCharacterCounter} = useChatContainerHeight({
    headerHeight: HEADER_HEIGHT,
    footerBaseHeight: FOOTER_BASE_HEIGHT,
    paddingBuffer: MARGIN_BUFFER,
    minInputHeight: 15,
    inputCounterVisibleHeight: INPUT_WITH_COUNTER_BUFFER,
    inputCounterHiddenHeight: INPUT_WITHOUT_COUNTER_BUFFER,
    enabled: openModal,
  });

  const handleBack = useCallback(() => {
    onClose();
    setQuestion("");
  }, [onClose]);

  const handleToggle = useCallback(() => {
    dispatch(setIsExpanded(!isExpanded));
  }, [dispatch, isExpanded]);

  const handleSubmitQuestion = useCallback((message) => {
    if (!question.trim() && !message) return;

    // Mark that user just submitted a message - should scroll to bottom
    justSubmittedRef.current = true;

    submitQuestion(
      typeof message === 'string' ? message : question.trim()
    );

    setQuestion("");
  }, [question, submitQuestion]);

  const isDisableSendMessage = () => {
    return !question?.trim() || isStreaming || question?.length > INPUT_MAX_LENGTH || !subscriptionConnected;
  }

  const isDisableChatBox = () => {
    return loading || currentStage === AIStage.INPUT_LOCKED || !subscriptionConnected || subscriptionConnecting;
  }
  
  const getPlaceholderText = () => {
    if (subscriptionConnecting) {
      return messages["ai_status_connecting"];
    }
    if (!subscriptionConnected) {
      return messages["ai_status_connection_lost"];
    }
    return messages["FSM_SETTINGS_MESSAGES_WRITE_MESSAGE"];
  }

  // Check if scroll is at or near bottom (within 50px threshold)
  const isScrollAtBottom = useCallback(() => {
    if (!scrollRef.current) return true;
    const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
    const threshold = 50; // pixels from bottom
    return scrollHeight - scrollTop - clientHeight < threshold;
  }, []);

  const handleClose = useCallback(() => {
    onClose();
    setQuestion("");
    dispatch(close());
  }, [onClose, dispatch]);

  // Handle loading more messages when scrolling to top
  const handleLoadMore = useCallback(() => {
    console.log('🔄 [AIMessageList] handleLoadMore called:', { 
      hasMoreMessages, 
      nextToken: !!nextToken, 
      conversationId, 
      loading,
      isLoadingHistory
    });
    if (hasMoreMessages && nextToken && conversationId && !loading && !isLoadingHistory) {
      console.log('🚀 [AIMessageList] Dispatching fetchMoreMessages...');
      // Save current scroll position before loading
      if (scrollRef.current) {
        setOldScrollTop(scrollRef.current.scrollTop);
        console.log('💾 [AIMessageList] Saved scroll position:', scrollRef.current.scrollTop);
      }
      setIsLoadingHistory(true);
      isLoadingHistoryRef.current = true;
      dispatch(fetchMoreMessages({ conversationId, nextToken, messages }));
    } else {
      console.log('⚠️ [AIMessageList] Not loading more - conditions not met');
    }
  }, [hasMoreMessages, nextToken, conversationId, loading, isLoadingHistory, dispatch]);

  // Recalculate container height when character counter visibility changes
  useEffect(() => {
    if (openModal) {
      recalculate();
      // Scroll to bottom after recalculation
      requestAnimationFrame(() => {
        if (scrollRef.current) {
          scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
      });
    }
  }, [openModal, showInputCharacterCounter, recalculate]);

  // Track scroll position before items change
  useEffect(() => {
    // Before items array changes, check if we're at bottom
    wasAtBottomRef.current = isScrollAtBottom();
  }, [items.length, isScrollAtBottom]);

  // Auto-scroll during streaming (THINKING/TOKEN chunk-by-chunk updates)
  useEffect(() => {
    if (isStreaming && openModal && scrollRef.current && !isLoadingHistoryRef.current) {
      // During streaming, continuously scroll to bottom to follow chunks
      if (wasAtBottomRef.current) {
        requestAnimationFrame(() => {
          if (scrollRef.current && !isLoadingHistoryRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
          }
        });
      }
    }
  }, [items, isStreaming, openModal]);

  // Detect when streaming completes or stage changes and scroll to bottom
  useEffect(() => {
    const wasStreaming = previousStreamingRef.current;
    const streamingJustEnded = wasStreaming && !isStreaming;
    
    const previousStage = previousStageRef.current;
    const stageChanged = previousStage !== currentStage;
    const isAnalyzing = currentStage === AIStage.ANALYZING;
    
    // Update the refs for next render
    previousStreamingRef.current = isStreaming;
    previousStageRef.current = currentStage;

    if (openModal && scrollRef.current && !isLoadingHistoryRef.current) {
      requestAnimationFrame(() => {
        if (scrollRef.current && !isLoadingHistoryRef.current) {
          // Scroll to bottom if:
          // 1. User just submitted a message (they want to see the response)
          // 2. User was already at bottom (following the conversation)
          // 3. Streaming just completed (show full message)
          // 4. Stage changed (new stage message appeared)
          // 5. Stage is ANALYZING (show initial processing state)
          if (justSubmittedRef.current || wasAtBottomRef.current || streamingJustEnded || stageChanged || isAnalyzing) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
            // Reset the flag after scrolling
            justSubmittedRef.current = false;
          }
          // If user was scrolled up, preserve their position (do nothing)
        }
      });
    }
  }, [openModal, loading, loadingState, initializationState, isStreaming, currentStage]);

  const groupedMessages = useMemo(() => {
    const groups = [];
    let currentGroup = null;

    items.forEach(item => {
      if (!currentGroup || currentGroup.sender !== item.sender) {
        // Start new group
        if (currentGroup) groups.push(currentGroup);
        currentGroup = {
          sender: item.sender,
          items: [item],
        };
      } else {
        // Add to current group
        currentGroup.items.push(item);
      }
    });

    if (currentGroup) groups.push(currentGroup);
    return groups;
  }, [items]);

  useEffect(() => {
    if (isLoadingHistory && scrollRef.current) {
      setIsLoadingHistory(false);
      isLoadingHistoryRef.current = false;

      if (!scrollRef.current) return;
      scrollRef.current.scrollTop = oldScrollTop;
      console.log('📍 [AIMessageList] Restore scroll positions:', {
        oldScrollTop,
        currentScrollTop: scrollRef.current.scrollTop,
        scrollHeight: scrollRef.current.scrollHeight
      });
      setOldScrollTop(0);
    }
  }, [groupedMessages]);

  // Calculate chat started label based on first message
  useEffect(() => {
    if (!items || items.length === 0) {
      setChatStartedLabel("");
      return;
    }

    // Find welcome message and real messages
    const welcomeMessages = items.filter(item => item.type === AiMessageType.WELCOME);
    const realMessages = items.filter(item => item.type !== AiMessageType.WELCOME);
    
    let firstMessage;
    
    if (realMessages.length === 0) {
      // No real messages yet, use welcome message timestamp (new conversation)
      firstMessage = welcomeMessages[0];
    } else {
      // Has real messages, use first real message timestamp
      const sortedItems = [...realMessages].sort((a, b) => new Date(a.createdOn) - new Date(b.createdOn));
      firstMessage = sortedItems[0];
    }
    
    if (!firstMessage) {
      setChatStartedLabel("");
      return;
    }
    
    if (firstMessage && firstMessage.createdOn) {
      const startMoment = moment(firstMessage.createdOn);
      const isToday = startMoment.isSame(moment(), 'day');
      
      if (isToday) {
        setChatStartedLabel(`${messages["chat_startedAt"]} ${startMoment.format('h:mm a').replace('am', 'a.m.').replace('pm', 'p.m.')}`);
      } else {
        setChatStartedLabel(`${messages["chat_startedOn"]} ${startMoment.format('MMMM D, YYYY')}`);
      }
    }
  }, [items, messages]);

  if (!openModal) return null;

  return (
    <Modal
      className="chat-thread chat-type-ai"
      open={openModal}
      size="small"
      position="right"
      title={messages["ai_title"]}
      iconBackClose
      handleBack={handleBack}
      useExpandMore
      handleExpand={handleToggle}
      handleClose={handleClose}
    >
      <ModalBody className="chat-thread-body">
        <div ref={bodyRef} style={{ display: 'flex', flexDirection: items.length === 0 ? 'column-reverse' : 'column', height: '100%' }}>
          <div ref={scrollRef} id="ai-thread-scroll" style={{ flex: items.length === 0 ? 'unset' : '1 1 0%' }} className="chat-thread-messages scrollbar-style">
          {/* Show skeleton during initial load */}
          {initializationState === InitializationState.SKELETON ? (
            <SkeletonChatMessages />
          ) : /* Show loading dots after skeleton */
          initializationState === InitializationState.LOADING_DOTS ? (
            <div className="chat-thread-scroller" style={{ display: 'flex', flexDirection: 'column-reverse', gap: '8px' }}>
              <AIChatMessages
                translated={messages}
                groupedMessages={[{
                  sender: "bot",
                  items: [
                    { bubbleType: AIMessageBubbleType.LOADING_DOTS }
                  ]
                }]}
              />
            </div>
          ) : /* Show messages or suggestions */
          loading && items.length === 0 ? (
            <SkeletonChatMessages />
          ) : (
            <InfiniteScroll
              scrollThreshold="20px"
              dataLength={items?.length || 0}
              inverse={true}
              hasMore={hasMoreMessages && !isLoadingHistory}
              next={handleLoadMore}
              loader={<div />}
              scrollableTarget="ai-thread-scroll"
              className="chat-thread-scroller"
              style={{
                gap: items.length === 0 ? "8px" : "16px",
                width: "100%"
              }}
            >
              {chatStartedLabel && items.length > 0 && !nextToken && (
                <div className="chat-thread-started">
                  <span className="chat-thread-system-label">
                    {chatStartedLabel}
                  </span>
                </div>
              )}
              <AIChatMessages
                translated={messages}
                groupedMessages={groupedMessages}
                handleSubmitQuestion={handleSubmitQuestion}
                handleRetryPrompt={handleRetryPrompt}
                isStreaming={isStreaming}
              />
            </InfiniteScroll>
          )}
          </div>
        </div>
      </ModalBody>
      <ModalFooter className="chat-thread-footer">
        {currentStage === AIStage.INPUT_LOCKED ? (
          <div className="chat-rate-limit-message">
            <p>{messages[`agent_stage_${error}`]}</p>
            <InputBase
                style={{display: 'none'}}
                ref={inputRef} />
          </div>
        ) : (
          <div className="chat-composer">
          <div
            className={`chat-composer-input-container ${showInputCharacterCounter ? 'multiline':'oneline'} ${isDisableChatBox() ? 'disabled':''}`}
          >
            <InputBase
              id="ai-thread-input"
              className="chat-composer-input scrollbar-style"
              placeholder={getPlaceholderText()}
              multiline
              ref={inputRef}
              value={question}
              disabled={isDisableChatBox()}
              autoFocus
              onKeyDown={(e) => {
                if (e.key !== "Enter" || isDisableSendMessage()) return;
                e.preventDefault();
                handleSubmitQuestion();
              }}
              onChange={(e) => setQuestion(e.target.value)}
              maxRows={6}
            />
            {showInputCharacterCounter && <div
            className={`length-indicator ${question.length > INPUT_MAX_LENGTH ? 'error' : ''}`}>
              {question.length}/{INPUT_MAX_LENGTH}
            </div>}
          </div>
          <IconButton
            disabled={isDisableSendMessage()}
            className="chat-composer-submit chat-type-ai"
            aria-label="submit"
            onClick={handleSubmitQuestion}
          >
            <NearMeOutlined />
          </IconButton>
        </div>
        )}
      </ModalFooter>
    </Modal>
  );
};

export default AIMessageList;
